import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Button,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetProcessDashboard } from "../../api/bpm/processDashboard";
import { useAuth } from "../../auth";
import {
  createDefaultDashboardPeriod,
  getDashboardDateRange,
  restoreDashboardPeriod,
} from "../../components/Dashboard/dashboardPeriod";
import DashboardPeriodSelector from "../../components/Dashboard/DashboardPeriodSelector";
import DashboardUserGroupFilter from "../../components/Dashboard/DashboardUserGroupFilter";
import ProcessDashboardMetrics, {
  type DashboardProcessFilter,
  type DashboardTaskFilter,
} from "../../components/Dashboard/ProcessDashboardMetrics";
import WelcomeHeader from "../../components/Dashboard/WelcomeHeader";

const DASHBOARD_PERIOD_STORAGE_KEY = "smartdocs.dashboard.period";
const DASHBOARD_USER_STORAGE_KEY = "smartdocs.dashboard.userId";
const DASHBOARD_GROUP_STORAGE_KEY = "smartdocs.dashboard.groupId";

export const Route = createFileRoute("/__auth/dashboard")({
  component: DashboardPage,
  staticData: {
    title: "routes.dashboard",
  },
});

export function DashboardPage() {
  const { t } = useTranslation();
  const { user, hasPermission } = useAuth();
  const canFilterUserGroup = hasPermission("dashboard.filter_user_group");
  const [period, setPeriod] = useState(() =>
    restoreDashboardPeriod(
      localStorage.getItem(DASHBOARD_PERIOD_STORAGE_KEY),
      createDefaultDashboardPeriod(),
    ),
  );
  const [userId, setUserId] = useState<string | undefined>(
    () => localStorage.getItem(DASHBOARD_USER_STORAGE_KEY) || undefined,
  );
  const [groupId, setGroupId] = useState<string | undefined>(
    () => localStorage.getItem(DASHBOARD_GROUP_STORAGE_KEY) || undefined,
  );
  const dateRange = useMemo(() => getDashboardDateRange(period), [period]);
  const navigate = Route.useNavigate();

  useEffect(() => {
    localStorage.setItem(DASHBOARD_PERIOD_STORAGE_KEY, JSON.stringify(period));
  }, [period]);

  useEffect(() => {
    if (userId) localStorage.setItem(DASHBOARD_USER_STORAGE_KEY, userId);
    else localStorage.removeItem(DASHBOARD_USER_STORAGE_KEY);
  }, [userId]);

  useEffect(() => {
    if (groupId) localStorage.setItem(DASHBOARD_GROUP_STORAGE_KEY, groupId);
    else localStorage.removeItem(DASHBOARD_GROUP_STORAGE_KEY);
  }, [groupId]);

  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    isFetching: isFetchingDashboard,
    error: dashboardError,
    refetch: refreshDashboard,
    dataUpdatedAt,
  } = useGetProcessDashboard(
    {
      ...(dateRange ?? { startDate: "", endDate: "" }),
      userId: canFilterUserGroup ? userId : undefined,
      groupId: canFilterUserGroup ? groupId : undefined,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    Boolean(user?.tenant && dateRange),
  );

  const handleTaskFilterClick = (filter: DashboardTaskFilter) => {
    navigate({
      to: "/tasks",
      search: {
        page: 1,
        size: 10,
        orderBy: "dueDate",
        order: "asc",
        ...filter,
        ...(dateRange
          ? {
              createTimeFrom: dateRange.startDate,
              createTimeTo: dateRange.endDate,
            }
          : {}),
      },
    });
  };

  const handleProcessClick = (processInstanceId: string) => {
    window.open(
      `/processes/${encodeURIComponent(processInstanceId)}/history/details`,
      "_blank",
      "noopener,noreferrer",
    );
  };
  const isInitialLoading = isLoadingDashboard && !dashboardData;
  const isRefreshing = isFetchingDashboard && Boolean(dashboardData);

  const handleProcessFilterClick = (filter: DashboardProcessFilter) => {
    const { periodField = "start", ...processFilter } = filter;
    const periodSearch = !dateRange
      ? {}
      : periodField === "end"
        ? { endTimeFrom: dateRange.startDate, endTimeTo: dateRange.endDate }
        : periodField === "incident"
          ? {
              incidentTimeFrom: dateRange.startDate,
              incidentTimeTo: dateRange.endDate,
            }
          : {
              startTimeFrom: dateRange.startDate,
              startTimeTo: dateRange.endDate,
            };
    navigate({
      to: "/processes",
      search: {
        page: 1,
        size: 10,
        orderBy: periodField === "end" ? "endTime" : "startTime",
        order: "desc",
        ...processFilter,
        ...periodSearch,
        dashboardScope: true,
      },
    });
  };

  const lastUpdatedLabel = dataUpdatedAt
    ? t("dashboard.processMetrics.lastUpdated", {
        date: new Intl.DateTimeFormat(undefined, {
          dateStyle: "short",
          timeStyle: "medium",
        }).format(dataUpdatedAt),
      })
    : t("dashboard.processMetrics.notUpdatedYet");

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        data-testid="dashboard-period-controls"
        sx={{
          mb: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minWidth: 0,
          maxWidth: "100%",
          alignItems: "stretch",
        }}
      >
        <Box
          data-testid="dashboard-update-controls"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
            mt: -2,
          }}
        >
          <Tooltip title={lastUpdatedLabel} arrow describeChild>
            <span>
              <Button
                data-testid="dashboard-refresh-button"
                variant="outlined"
                size="small"
                startIcon={<RefreshOutlinedIcon />}
                disabled={isFetchingDashboard}
                onClick={() => refreshDashboard()}
                sx={{
                  minWidth: 96,
                  height: 28,
                  px: 1.25,
                  fontSize: "0.75rem",
                  flexShrink: 0,
                }}
              >
                {t("dashboard.processMetrics.refresh")}
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box
          data-testid="dashboard-header-row"
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <WelcomeHeader />
          <Box
            data-testid="dashboard-filter-controls"
            sx={{
              display: "flex",
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: 1.5,
              alignItems: "center",
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            {canFilterUserGroup && (
              <DashboardUserGroupFilter
                userId={userId}
                groupId={groupId}
                onChangeUser={setUserId}
                onChangeGroup={setGroupId}
              />
            )}
            <DashboardPeriodSelector value={period} onChange={setPeriod} />
          </Box>
        </Box>
      </Box>

      {isInitialLoading ? (
        <DashboardInitialLoading />
      ) : (
        <>
          {dashboardError ? (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {t("dashboard.processMetrics.error")}
            </Alert>
          ) : null}

          <Box
            aria-busy={isRefreshing}
            sx={{ position: "relative", minWidth: 0, maxWidth: "100%" }}
          >
            {isRefreshing ? (
              <LinearProgress
                aria-label={t("dashboard.processMetrics.loadingIndicators")}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  mb: 1,
                  borderRadius: 1,
                }}
              />
            ) : null}
            <Box
              sx={{
                opacity: isRefreshing ? 0.72 : 1,
                pointerEvents: isRefreshing ? "none" : "auto",
                transition: "opacity 160ms ease",
              }}
            >
              <ProcessDashboardMetrics
                data={dashboardData}
                loading={false}
                onTaskFilterClick={handleTaskFilterClick}
                onProcessFilterClick={handleProcessFilterClick}
                onProcessClick={handleProcessClick}
              />
            </Box>
          </Box>
        </>
      )}
    </Container>
  );
}

function DashboardInitialLoading() {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={0}
      role="status"
      aria-live="polite"
      sx={{
        minHeight: { xs: 360, md: 480 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Stack alignItems="center" textAlign="center" gap={2}>
        <CircularProgress size={44} />
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {t("dashboard.processMetrics.loadingIndicators")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {t("dashboard.processMetrics.loadingIndicatorsDescription")}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
